import { ds } from '@/components/lib/design.system';
import { PeriodScore as EventPeriodScore, SportSpecific } from '@/feature/event-details-sbk/types';
import { getTeamOrder } from '@/utils/sport-utils';

import { Box } from '../../lib/components';
import { PeriodScore, ScoreBoardGridProps, ScoreboardEvent, StoreCacheSlice } from '../types';
import { buildTeamInfo } from './build-team-info';

export const TENNIS_PERIOD_SCORE_TYPE = 'tennis';

const SERVING_DOT_SIZE = 8;

/** Green dot marking the serving player, shared by the matchup card and the hud scoreboard. */
export const TennisServingDot = () => (
    <Box
        margin="s2"
        width={SERVING_DOT_SIZE}
        height={SERVING_DOT_SIZE}
        borderRadius="r64"
        backgroundColor={ds.colorToken.background.accent.lime.strong}
    />
);

/**
 * Side currently serving ('home' | 'away'), or undefined when the match isn't live or the data
 * is unavailable. current_serving_team arrives as 'HOME' | 'AWAY'; team sides are lowercase.
 */
export const getServingSide = (sportSpecific: SportSpecific | undefined, isLive: boolean): string | undefined =>
    isLive ? sportSpecific?.current_serving_team?.toLowerCase() : undefined;

// Tennis game points arrive from the API as raw numbers. 50 represents Advantage after deuce.
// During a tie-break the score counts raw points, so the display mapping doesn't apply.
const GAME_SCORE_DISPLAY: Record<number, string> = { 0: '0', 15: '15', 30: '30', 40: '40', 50: 'AD' };
export const formatTennisGameScore = (score: number, isTieBreak = false): string =>
    isTieBreak ? String(score) : GAME_SCORE_DISPLAY[score] ?? String(score);

/**
 * Tie-break points a side scored in a completed 7-6 set, or undefined when the set didn't go to
 * a tie-break or the point data is unavailable. The tie-break is the set's final game, so its
 * key in points_won_per_game is the number of games played.
 */
export const getTieBreakPoints = (
    pointsWonPerGame: SportSpecific['points_won_per_game'],
    periodScore: EventPeriodScore,
    side: 'home' | 'away'
): number | undefined => {
    const { home_score, away_score, period_number } = periodScore;
    const wentToTieBreak = (home_score === 7 && away_score === 6) || (home_score === 6 && away_score === 7);
    if (!wentToTieBreak) {
        return undefined;
    }
    const tieBreakGame = pointsWonPerGame?.[period_number.toLowerCase()]?.[String(home_score + away_score)];
    if (!tieBreakGame) {
        return undefined;
    }
    return Object.values(tieBreakGame).filter(pointWinner => pointWinner === side).length;
};

export const buildTennisScoreboardProps = (
    event: ScoreboardEvent,
    cache: StoreCacheSlice
): Omit<ScoreBoardGridProps, 'variant' | 'matchDetails'> | undefined => {
    const { periodScores, sportSpecific, eventState } = cache;

    const [firstSide, secondSide] = getTeamOrder(event.sport.name);
    const { team1, team2 } = buildTeamInfo(event, firstSide, secondSide);

    if (eventState === 'pregame') {
        return { team1, team2, breakdown: [], eventState };
    }

    const tennisPeriodScores = (periodScores ?? []).filter(p => p.type === TENNIS_PERIOD_SCORE_TYPE);
    const isLive = eventState === 'live';

    const breakdown: PeriodScore[] = tennisPeriodScores.map((p, index) => ({
        team1Score: { away: p.away_score, home: p.home_score }[firstSide],
        team2Score: { away: p.away_score, home: p.home_score }[secondSide],
        team1SubScore: getTieBreakPoints(sportSpecific?.points_won_per_game, p, firstSide),
        team2SubScore: getTieBreakPoints(sportSpecific?.points_won_per_game, p, secondSide),
        label: String(index + 1),
    }));

    const activePeriodIndex = isLive ? tennisPeriodScores.length - 1 : undefined;
    const servingSide = getServingSide(sportSpecific, isLive);
    const currentGameScore = sportSpecific?.current_game_score;
    const gameScoreColumn: PeriodScore | undefined =
        isLive && currentGameScore !== undefined
            ? {
                  team1Score: formatTennisGameScore(currentGameScore[firstSide], sportSpecific?.is_match_in_tie_break),
                  team2Score: formatTennisGameScore(currentGameScore[secondSide], sportSpecific?.is_match_in_tie_break),
              }
            : undefined;

    return {
        team1,
        team2,
        team1Indicator: servingSide === firstSide ? <TennisServingDot /> : undefined,
        team2Indicator: servingSide === secondSide ? <TennisServingDot /> : undefined,
        breakdown,
        gameScoreColumn,
        activePeriodIndex,
        eventState,
    };
};
